import React, { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { format } from 'date-fns';

import { RootState } from '../../../core/state/state';
import FormItem from '../../../components/form/form-item/FormItem';
import Input from '../../../components/form/input/Input';
import Button from '../../../components/form/button/Button';
import Form from '../../../components/form/form/Form';
import { ProjectSettings } from '../../../core/state/project/types';
import { getProjectSettings, saveProjectSettings } from '../../../core/state/project/asyncActions';
import RangeTimePicker from '../../../components/form/range-time-picker/RangeTimePicker';
import CheckBox from '../../../components/form/checkbox/CheckBox';
import { getTimeDate } from '../../../core/utils/date';
import { TIME_FORMAT_BACKEND } from '../../../core/constants/date';
import ProjectSettingsSlider from './ProjectSettingsSlider';
import { convertPositiveNumber, masterPinFormatting } from './helpers';
import { SettingsExpanded } from '../../../core/types/settings/SettingsExpanded';
import UnsavedChangesModal from '../../components/unsaved-changes-modal/UnsavedChangesModal';
import { DE_OPENING_QUOTE, DE_CLOSING_QUOTE } from '../../../../locales/de/SpecialCharacters';
import { isReadAllRole } from '../../../core/state/user/selectors';

interface Props {
  isExpanded: SettingsExpanded;
  setExpanded: Dispatch<SetStateAction<SettingsExpanded>>;
}

const ProjectSettingsContent: React.FC<Props> = ({ isExpanded, setExpanded }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const projectSettings = useSelector((state: RootState) => state.projects?.projectSettings);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isUserReader = useSelector(isReadAllRole);

  const { control, handleSubmit, setValue, watch, formState } = useForm<ProjectSettings>({
    defaultValues: {
      operatingHoursFrom: '',
      operatingHoursTo: '',
      deliveryDurationMin: 0.5,
      deliveryDurationMax: 2,
      automaticConfirmationTime: 168,
      bookingDeadline: 100,
      isInBarrierSystem: false,
      includeSaturdays: false,
      includeSundays: false
    }
  });

  const onSubmit = handleSubmit(async (data) => {
    await dispatch(saveProjectSettings(Number(projectId), data));
    await dispatch(getProjectSettings(Number(projectId)));
    setExpanded({ ...isExpanded, settings: false });
  });

  const onCancel = () => {
    setExpanded({ ...isExpanded, settings: false });
  };

  React.useEffect(() => {
    dispatch(getProjectSettings(Number(projectId)));
  }, [dispatch, projectId]);

  React.useEffect(() => {
    if (projectSettings) {
      (Object.keys(projectSettings) as Array<keyof typeof projectSettings>).forEach((item) => {
        setValue(item, projectSettings[item] ?? '');
      });
    }
  }, [projectSettings, setValue]);

  const operatingHoursFrom = watch('operatingHoursFrom');
  const operatingHoursTo = watch('operatingHoursTo');
  const deliveryDurationMin = watch('deliveryDurationMin');
  const deliveryDurationMax = watch('deliveryDurationMax');

  return (
    <div className="project-settings-content">
      <Form onSubmit={onSubmit}>
        <FormItem
          text={t('projectSettings.projectSettings.operationHours')}
          description={t('projectSettings.projectSettings.operationHours.description')}
        >
          <Controller
            name="operatingHoursFrom"
            control={control}
            render={() => (
              <RangeTimePicker
                onChange={(range) => {
                  setValue('operatingHoursFrom', format(range.firstValue, TIME_FORMAT_BACKEND), { shouldDirty: true });
                  setValue('operatingHoursTo', format(range.secondValue, TIME_FORMAT_BACKEND), { shouldDirty: true });
                }}
                firstValue={operatingHoursFrom ? getTimeDate(operatingHoursFrom) : getTimeDate('06:00')}
                secondValue={operatingHoursTo ? getTimeDate(operatingHoursTo) : getTimeDate('17:00')}
                excludeDisablingHours
              />
            )}
          />
        </FormItem>
        <FormItem
          text={t('projectSettings.projectSettings.deliveryDuration')}
          description={t('projectSettings.projectSettings.deliveryDuration.description')}
        >
          <Controller
            name="deliveryDurationMin"
            control={control}
            render={() => (
              <ProjectSettingsSlider
                onChange={(changedValue) => {
                  const [firstValue, secondValue] = changedValue;
                  setValue('deliveryDurationMin', firstValue, { shouldDirty: true });
                  setValue('deliveryDurationMax', secondValue, { shouldDirty: true });
                }}
                value={[deliveryDurationMin, deliveryDurationMax]}
              />
            )}
          />
        </FormItem>
        <FormItem
          text={t('projectSettings.projectSettings.bookingDeadline')}
          description={t('projectSettings.projectSettings.bookingDeadline.description')}
        >
          <Controller
            name="bookingDeadline"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                className="project-settings__input"
                type="number"
                onBlur={(e) => {
                  if (Number(e.target.value) < 10) {
                    onChange(10);
                  }
                }}
                onChange={(e) => {
                  onChange(convertPositiveNumber(e.target.value, 100));
                }}
                id="bookingDeadline"
                name="bookingDeadline"
                value={value ?? ''}
              />
            )}
          />
        </FormItem>
        <FormItem
          text={t('projectSettings.projectSettings.automaticConfirmationTime')}
          description={t('projectSettings.projectSettings.automaticConfirmationTime.description')}
        >
          <Controller
            name="automaticConfirmationTime"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                className="project-settings__input"
                type="number"
                onChange={(e) => {
                  onChange(convertPositiveNumber(e.target.value, 168));
                }}
                id="automaticConfirmationTime"
                name="automaticConfirmationTime"
                value={value ?? ''}
              />
            )}
          />
          <div className="d-flex mt-3">
            <Controller
              name="includeSaturdays"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CheckBox
                  id="includeSaturdays"
                  name="includeSaturdays"
                  checked={!value}
                  onChange={(e) => {
                    e.target.checked = !e.target.checked;
                    onChange(e);
                  }}
                />
              )}
            />
            <span>{t('projectSettings.projectSettings.automaticConfirmationTime.saturday')}</span>
          </div>
          <div className="d-flex mt-2">
            <Controller
              name="includeSundays"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CheckBox
                  id="includeSundays"
                  name="includeSundays"
                  checked={!value}
                  onChange={(e) => {
                    e.target.checked = !e.target.checked;
                    onChange(e);
                  }}
                />
              )}
            />
            <span>{t('projectSettings.projectSettings.automaticConfirmationTime.sunday')}</span>
          </div>
        </FormItem>
        <FormItem
          text={t('projectSettings.projectSettings.barrierSystem')}
          description={t('projectSettings.projectSettings.barrierSystem.description', { DE_OPENING_QUOTE, DE_CLOSING_QUOTE })}
        >
          <Controller
            name="isInBarrierSystem"
            control={control}
            render={({ field: { onChange, value } }) => (
              <CheckBox id="isInBarrierSystem" name="isInBarrierSystem" checked={value} onChange={onChange} />
            )}
          />
        </FormItem>
        <FormItem text={t('projectSettings.projectSettings.masterPin')} description={t('projectSettings.projectSettings.masterPin.description')}>
          <Controller
            name="masterPin"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                className="project-settings__input"
                type="text"
                onChange={(e) => {
                  onChange(masterPinFormatting(e.target.value));
                }}
                id="masterPin"
                name="masterPin"
                value={value ?? ''}
              />
            )}
          />
        </FormItem>
        {!isUserReader && (
          <div className="row">
            <div className="offset-3 col-9">
              <Button buttonTypes={['blue-outline', 'transparent']} className="mr-2" onClick={onCancel}>
                {t('common.cancel')}
              </Button>
              <Button buttonTypes={['blue']} type="submit">
                {t('common.saveAndClose')}
              </Button>
            </div>
          </div>
        )}

        <UnsavedChangesModal showIf={formState.isDirty} onSaveExit={() => onSubmit()} />
      </Form>
    </div>
  );
};

export default ProjectSettingsContent;
